<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'Tab' + tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Users'"
          @tabClick="(index: number) => tabChange(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="getTabDirtyStatus(tab.id)">*</sup>
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="'keepAlive' + index"
            v-if="index === 0"
          >
            <UserList
              ref="userListRef"
              :key="'userList' + tab.id"
              v-show="selectedTab === index"
              @create="openCreateFormTab"
              @edit="openEditFormTab"
              @deleteById="handleDeleteUser"
            />
          </keep-alive>
          <keep-alive
            :key="'keepAlive' + tab.id"
            v-else
          >
            <UserDetails
              :key="'userDetail' + tab.id"
              v-show="selectedTab === index"
              :current-user-id="tab.id"
              :tab-name="tab.name"
              @close-current-tab="closeCurrentTab"
              @deleteById="handleDeleteUser"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
  </section>
  <UserDelete
    @delete="deleteUser"
    :is-used="0"
    :entity="'Users'"
    ref="deleteModalRef"
  />
</template>

<script setup lang="ts">
  import { nextTick, Ref, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import UserList from '@/app/features/admin/user/components/UserList.vue';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import { useUserStore } from '@/app/features/admin/user/user-store';
  import { useI18n } from 'vue-i18n';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import UserDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import UserDetails from '@/app/features/admin/user/components/UserDetails.vue';
  import { AdminAddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';

  const { t } = useI18n();
  const userStore = useUserStore();
  const alertStore = useAlertStore();
  const userListRef = ref<InstanceType<typeof UserList>[]>([]);
  const { selectedTab, tabs } = storeToRefs(userStore);
  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const userIdToDelete = ref<number | string | null>(null);
  const getTabDirtyStatus = (userId: string | number) => {
    let dirtyFlag = false;
    let dirtyTab = userStore.userDetails.get(userId)?.dirtyTab;
    if (typeof dirtyTab !== 'undefined') {
      for (const [, dirtyTabValue] of dirtyTab ?? new Map().entries()) {
        if (dirtyTabValue) {
          dirtyFlag = true;
        }
      }
    }
    return dirtyFlag;
  };

  const tabChange = (index: number) => {
    selectedTab.value = index;
  };

  const closeCurrentTab = (index: number) => {
    userStore.closeTab(index);
    userListRef.value[0]?.refreshUsers();
    if (index === 1 && index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        userListRef.value[0]?.refreshUsers();
      });
    } else if (index <= selectedTab.value) {
      userListRef.value[0]?.refreshUsers();
    }
  };

  const closeTab = (index: number, tab: TabHeader) => {
    userStore.closeTab(tab.id);
  };

  const openCreateFormTab = async () => {
    await userStore.createUserTab({
      id: -1,
      name: 'New User',
      isNew: true,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const openEditFormTab = async ({
    id,
    name,
  }: AdminAddTabEvent): Promise<void> => {
    await userStore.createUserTab({
      id: id,
      name: name,
      isNew: false,
      dirtyTab: new Map<string, boolean>(),
    });
  };

  const handleDeleteUser = (userId: number | string) => {
    userIdToDelete.value = userId;
    deleteModalRef.value?.changeModalVisibility();
  };

  const deleteUser = async () => {
    if (userIdToDelete.value == null) return;
    await userStore.deleteUser(userIdToDelete.value);
    userListRef.value[0]?.refreshUsers();
    closeCurrentTab(userIdToDelete.value as number);
    alertStore.showNotification(t('userDeleted'), t('User'), Motif.SUCCESS);
  };
</script>

<style scoped>
  .user-page {
    padding: 20px;
  }

  h1 {
    color: #333;
    margin-bottom: 16px;
  }
</style>
